import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { InventoryRow, StockStatus } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { PageHeader } from '@/components/PageHeader';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { SectionCard } from '@/components/SectionCard';
import { ErrorState } from '@/components/States';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { ExportMenu } from '@/features/portal/ExportMenu';
import { usePortal } from '@/features/portal/PortalContext';
import { fetchInventory, portalKeys } from '@/features/portal/api';
import { toReportRange } from '@/features/portal/filters';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { formatCount, formatMoney } from '@/lib/format';

const FILTER_FIELDS = ['Product', 'Category', 'Status'] as const;

const TOGGLEABLE = [
  'branch',
  'category',
  'unit',
  'status',
  'quantity',
  'reorderLevel',
  'sellingPrice',
  'stockValue',
] as const;

const COLUMN_LABELS: Record<string, string> = {
  branch: 'Branch',
  category: 'Category',
  unit: 'Unit',
  status: 'Status',
  quantity: 'Qty',
  reorderLevel: 'Reorder at',
  sellingPrice: 'Price',
  stockValue: 'Stock value',
};

/** Shop-facing wording; the API's own values are terse enum members. */
const STATUS_LABELS: Record<StockStatus, string> = {
  ok: 'In stock',
  low: 'Low',
  out: 'Out of stock',
  untracked: 'Not tracked',
};

const STATUS_CLASSES: Record<StockStatus, string> = {
  ok: 'bg-emerald-50 text-emerald-700',
  low: 'bg-amber-50 text-amber-700',
  out: 'bg-rose-50 text-rose-700',
  // Deliberately grey: "not tracked" is an absence of a figure, not a
  // problem to chase, and colouring it would put it in the same visual
  // bucket as genuinely empty stock.
  untracked: 'bg-slate-100 text-slate-500',
};

function StatusPill({ status }: { status: StockStatus }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

/**
 * Stock on hand per product, as the tills last reported it.
 *
 * The portal's filter bar sits above this page like any other, but only its
 * branch/till selection changes the figures — there is no stock history in
 * the cloud to slice by date, so the date range is deliberately ignored (see
 * `ReportsService.inventory`) and the page says so rather than showing
 * numbers that look range-scoped and are not.
 */
export function InventoryPage() {
  const { currency, filters, multiBranch } = usePortal();
  const range = useMemo(() => toReportRange(filters.filters), [filters.filters]);
  const toolbar = useReportToolbar();
  const layout = useReportLayout('inventory', TOGGLEABLE);

  const inventoryQuery = useQuery({
    queryKey: portalKeys.inventory(range),
    queryFn: ({ signal }) => fetchInventory(range, signal),
  });

  const report = inventoryQuery.data;
  const reportCurrency = report?.currency || currency;

  const rows = useMemo(
    () =>
      applyClientFilters(report?.rows ?? [], {
        search: toolbar.search,
        filterRows: toolbar.filterRows,
        matchAny: toolbar.matchAny,
        fieldValue: (row, field) =>
          field === 'Product'
            ? row.productName
            : field === 'Category'
              ? row.categoryName
              : field === 'Status'
                ? STATUS_LABELS[row.status]
                : undefined,
        searchValues: (row) => [row.productName, row.categoryName],
      }),
    [report, toolbar.search, toolbar.filterRows, toolbar.matchAny],
  );

  const baseColumns = useMemo<Array<Column<InventoryRow>>>(() => {
    const money = (value: string) => formatMoney(value, reportCurrency);
    /** An untracked product has no stock figure — a dash, not a zero. */
    const untracked = <span className="text-slate-300">—</span>;

    return [
      {
        key: 'productName',
        header: 'Product',
        pinned: true,
        cell: (row) => <p className="font-medium text-slate-900">{row.productName}</p>,
      },
      {
        key: 'branch',
        header: 'Branch',
        cell: (row) => <span className="text-slate-500">{row.branchName || '—'}</span>,
      },
      {
        key: 'category',
        header: 'Category',
        cell: (row) => <span className="text-slate-500">{row.categoryName || '—'}</span>,
      },
      {
        key: 'unit',
        header: 'Unit',
        cell: (row) => <span className="text-slate-500">{row.unit || '—'}</span>,
      },
      {
        key: 'status',
        header: 'Status',
        pinned: true,
        cell: (row) => <StatusPill status={row.status} />,
      },
      {
        key: 'quantity',
        header: 'Qty',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) =>
          row.status === 'untracked' ? untracked : formatCount(row.quantity),
      },
      {
        key: 'reorderLevel',
        header: 'Reorder at',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) =>
          row.status === 'untracked' ? untracked : row.reorderLevel,
      },
      {
        key: 'sellingPrice',
        header: 'Price',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => money(row.sellingPrice),
      },
      {
        key: 'stockValue',
        header: 'Stock value',
        align: 'right',
        cellClassName: 'tabular-nums font-medium text-slate-900',
        cell: (row) =>
          row.status === 'untracked' ? untracked : money(row.stockValue),
      },
    ];
  }, [reportCurrency]);

  const columns = useMemo(() => {
    const resolved = applyReportLayout(baseColumns, layout);
    return multiBranch ? resolved : resolved.filter((column) => column.key !== 'branch');
  }, [baseColumns, layout, multiBranch]);

  const header = (
    <PageHeader
      title="Inventory"
      description="Stock on hand as the tills last reported it. Not affected by the date range."
      actions={
        <ExportMenu
          report="inventory"
          range={range}
          disabled={inventoryQuery.isPending || inventoryQuery.isError}
        />
      }
    />
  );

  if (inventoryQuery.isError) {
    return (
      <>
        {header}
        <div className="card">
          <ErrorState
            error={inventoryQuery.error}
            onRetry={() => void inventoryQuery.refetch()}
            retrying={inventoryQuery.isFetching}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {header}

      <div className="space-y-6">
        <AsyncSection
          state={inventoryQuery}
          skeleton={
            <KpiGrid>
              {Array.from({ length: 4 }).map((_, index) => (
                <KpiTileSkeleton key={index} />
              ))}
            </KpiGrid>
          }
        >
          {(data) => (
            <KpiGrid>
              <KpiTile label="Products" value={formatCount(data.count)} />
              <KpiTile
                label="Stock value"
                value={formatMoney(data.totalStockValue, data.currency || currency)}
              />
              <KpiTile label="Low stock" value={formatCount(data.lowStockCount)} />
              <KpiTile label="Out of stock" value={formatCount(data.outOfStockCount)} />
            </KpiGrid>
          )}
        </AsyncSection>

        <SectionCard
          headingId="inventory-table"
          title="Stock on hand"
          count={rows.length}
          description="Products the tills track, with their reorder level and current value."
        >
          <ReportToolbar>
            <ReportSearchField
              value={toolbar.search}
              onChange={toolbar.setSearch}
              placeholder="Product or category"
            />
            <ReportFiltersButton
              fields={FILTER_FIELDS}
              applied={toolbar.filterRows}
              matchAny={toolbar.matchAny}
              onApply={toolbar.applyFilters}
            />
            <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
          </ReportToolbar>
          <AsyncSection
            state={inventoryQuery}
            skeleton={<TableSkeleton rows={6} columns={7} />}
            isEmpty={() => rows.length === 0}
            emptyTitle={
              toolbar.search ? `No products match “${toolbar.search}”` : 'No products yet'
            }
            emptyDescription="Products appear here once a till has synced its catalog."
          >
            {() => (
              <DataTable
                caption="Products with category, stock status, quantity, reorder level and value"
                columns={columns}
                rows={rows}
                rowKey={(row) => row.id}
                layout={layout}
              />
            )}
          </AsyncSection>
        </SectionCard>
      </div>
    </>
  );
}
